import { GenericTaskType } from '@compass/client/types/generic_task';
import { TemplateTargetType } from '@compass/client/types/template';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { Textarea } from '@components/ui/textarea';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { useCreateGenericTask } from '@/hooks/useGenericTask';
import { useRenderTemplate, useTemplates } from '@/hooks/useTemplates';
import { useStore } from '@/store/store';
import type { ActionDataParams } from '@/types/actions';

import { DatePicker } from './ui/date-picker';

interface TaskFormData {
  recipient: string;
  startDate: string;
  subject: string;
  message: string;
}

const CUSTOM_TEMPLATE_ID = 'custom';

interface ActionTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  caseRecordId: string;
  action: string;
  onActionSelect: (action: string, caseId: string, actionDataParams?: ActionDataParams) => void;
  title?: string;
  dialogDescription?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

export function ActionTaskDialog({
  isOpen,
  onClose,
  caseRecordId,
  action,
  onActionSelect,
  title = 'Create Task',
  dialogDescription = 'Task details',
  confirmButtonText = 'Create Task',
  cancelButtonText = 'Cancel',
}: ActionTaskDialogProps) {
  const createGenericTask = useCreateGenericTask();
  const renderTemplate = useRenderTemplate();
  const selectedProgramId = useStore(state => state.selectedProgramId);

  // Fetch task templates scoped to the user's program
  const { data: templatesData, isLoading: isLoadingTemplates } = useTemplates({
    programId: selectedProgramId ?? undefined,
    targetType: TemplateTargetType.TEMPLATE_TARGET_TYPE_GENERIC_TASK,
    enabled: isOpen,
  });

  const templates = templatesData?.templates ?? [];
  const hasTemplates = templates.length > 0;

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(CUSTOM_TEMPLATE_ID);
  const [formData, setFormData] = useState<TaskFormData>({
    recipient: '',
    startDate: '',
    subject: '',
    message: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Track if template is being rendered
  const isRenderingTemplate = renderTemplate.isPending;

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTemplateId(CUSTOM_TEMPLATE_ID);
      setFormData({ recipient: '', startDate: '', subject: '', message: '' });
      setErrorMessage(null);
    }
  }, [isOpen]);

  const handleTemplateSelect = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);

      if (templateId === CUSTOM_TEMPLATE_ID) {
        // User chose custom - clear form for manual entry
        setFormData({
          recipient: '',
          startDate: '',
          subject: '',
          message: '',
        });
        return;
      }

      // Render the template with case record context
      renderTemplate.mutate(
        { templateId, caseRecordId },
        {
          onSuccess: data => {
            const renderedTask = data.rendered?.generic_task?.gastro_create_task;
            setFormData(prev => ({
              ...prev,
              subject: renderedTask?.subject ?? '',
              message: renderedTask?.message ?? '',
            }));
          },
          onError: error => {
            console.error('Failed to render template:', error);
            setErrorMessage('Failed to load template. Please try again or use a custom template.');
          },
        },
      );
    },
    [caseRecordId, renderTemplate],
  );

  const handleConfirm = useCallback(() => {
    // Validate required fields
    if (!formData.recipient.trim() || !formData.subject.trim()) {
      return;
    }
    setErrorMessage(null);

    // Create the task entity
    createGenericTask.mutate(
      {
        caseRecordId,
        data: {
          type: GenericTaskType.GENERIC_TASK_TYPE_GASTRO_CREATE_TASK,
          input: {
            recipient: formData.recipient,
            subject: formData.subject,
            start_date: formData.startDate,
            message: formData.message,
          },
        },
      },
      {
        onSuccess: data => {
          const newTaskId = data.id;
          if (!newTaskId) {
            console.error('Task created but no ID returned');
            return;
          }

          // Reset state
          setFormData({ recipient: '', startDate: '', subject: '', message: '' });
          setSelectedTemplateId(CUSTOM_TEMPLATE_ID);
          // Close dialog
          onClose();
          // Trigger action with task_id
          onActionSelect(action, caseRecordId, { task_id: newTaskId });
        },
        onError: error => {
          console.error('Failed to create task:', error);
          setErrorMessage('Failed to create task. Please try again.');
        },
      },
    );
  }, [formData, caseRecordId, action, onActionSelect, onClose, createGenericTask]);

  const handleClose = useCallback(() => {
    // Reset state on cancel
    setFormData({
      recipient: '',
      startDate: '',
      subject: '',
      message: '',
    });
    setSelectedTemplateId(CUSTOM_TEMPLATE_ID);
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  const updateField = useCallback((field: keyof TaskFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const isFormValid = formData.recipient.trim() !== '' && formData.subject.trim() !== '';

  const isSubmitting = createGenericTask.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Template Selection - Loading State */}
          {isLoadingTemplates && (
            <div className="grid gap-2">
              <label className="text-sm font-medium">Template</label>
              <div className="flex h-10 items-center rounded-md border border-input bg-background px-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading templates...</span>
              </div>
            </div>
          )}

          {/* Template Selection - visible when templates exist */}
          {!isLoadingTemplates && hasTemplates && (
            <div className="grid gap-2">
              <label htmlFor="template-select" className="text-sm font-medium">
                Template
              </label>
              <Select
                value={selectedTemplateId}
                onValueChange={handleTemplateSelect}
                disabled={isSubmitting || isRenderingTemplate}
              >
                <SelectTrigger id="template-select">
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CUSTOM_TEMPLATE_ID}>
                    <span className="text-muted-foreground">Custom</span>
                  </SelectItem>
                  {templates
                    .filter(template => template.id)
                    .map(template => (
                      <SelectItem key={template.id} value={template.id!}>
                        {template.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Loading state when rendering template */}
          {isRenderingTemplate && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading template...</span>
            </div>
          )}

          {/* Form fields - visible when not rendering template */}
          {!isRenderingTemplate && (
            <>
              {/* Recipient */}
              <div className="grid gap-2">
                <label htmlFor="task-recipient" className="text-sm font-medium">
                  Recipient
                  <span className="text-destructive ml-1">*</span>
                </label>
                <Input
                  id="task-recipient"
                  value={formData.recipient}
                  onChange={e => updateField('recipient', e.target.value)}
                  placeholder="Enter recipient email or name"
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>

              {/* Start Date */}
              <div className="grid gap-2">
                <label htmlFor="task-startDate" className="text-sm font-medium">
                  Start Date
                </label>
                <DatePicker
                  value={formData.startDate}
                  onChange={date =>
                    updateField('startDate', date ? format(date, 'MM-dd-yyyy') : '')
                  }
                  disabled={isSubmitting}
                  className="h-11"
                />
              </div>

              {/* Subject */}
              <div className="grid gap-2">
                <label htmlFor="task-subject" className="text-sm font-medium">
                  Subject
                  <span className="text-destructive ml-1">*</span>
                </label>
                <Input
                  id="task-subject"
                  value={formData.subject}
                  onChange={e => updateField('subject', e.target.value)}
                  placeholder="Enter task subject"
                  disabled={isSubmitting}
                />
              </div>

              {/* Description */}
              <div className="grid gap-2">
                <label htmlFor="task-message" className="text-sm font-medium">
                  Message
                </label>
                <Textarea
                  id="task-message"
                  value={formData.message}
                  onChange={e => updateField('message', e.target.value)}
                  placeholder="Enter task message (optional)"
                  className="min-h-[100px]"
                  disabled={isSubmitting}
                />
              </div>
            </>
          )}
        </div>

        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {cancelButtonText}
          </Button>
          <Button
            data-analytics-id="task:create"
            onClick={handleConfirm}
            disabled={isSubmitting || isRenderingTemplate || !isFormValid}
          >
            {isSubmitting ? 'Processing...' : confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
